import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Loader2, Pill } from "lucide-react";
import { toast } from "sonner";

const FREQUENCY_OPTIONS = [
  "1-0-0", "0-1-0", "0-0-1",
  "1-0-1", "1-1-0", "0-1-1",
  "1-1-1", "SOS",
];

const INSTRUCTION_OPTIONS = [
  "After food",
  "Before food",
  "With water",
  "At bedtime",
  "On empty stomach",
];

function blankRow() {
  return { name: "", dosage: "", frequency: "1-0-1", duration: "5 days", instructions: "After food" };
}

export default function PrescriptionDialog({ open, onOpenChange, patientId, doctorName, onSaved, initial }) {
  const isEdit = !!initial;
  const [diagnosis, setDiagnosis] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [medicines, setMedicines] = useState([blankRow()]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setDiagnosis(initial.diagnosis || "");
        setDate(initial.date || new Date().toISOString().slice(0, 10));
        setMedicines(initial.medicines?.length ? initial.medicines : [blankRow()]);
        setNotes(initial.notes || "");
      } else {
        setDiagnosis("");
        setDate(new Date().toISOString().slice(0, 10));
        setMedicines([blankRow()]);
        setNotes("");
      }
      setError("");
    }
  }, [open, initial]);

  const updateRow = (i, key, val) => {
    setMedicines((prev) => prev.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  };

  const addRow = () => setMedicines((prev) => [...prev, blankRow()]);
  const removeRow = (i) => setMedicines((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const cleanMeds = medicines.filter((m) => m.name?.trim());
    if (cleanMeds.length === 0) {
      setError("Add at least one medicine.");
      return;
    }
    setSubmitting(true);
    try {
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/prescriptions/${initial.prescription_id}`, {
          diagnosis,
          date,
          medicines: cleanMeds,
          notes,
        });
        saved = data;
        toast.success("Prescription updated");
      } else {
        const { data } = await api.post("/prescriptions", {
          patient_id: patientId,
          diagnosis,
          date,
          medicines: cleanMeds,
          notes,
        });
        saved = data;
        toast.success("Prescription saved");
      }
      onOpenChange(false);
      onSaved && onSaved(saved, { isEdit });
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="prescription-dialog" className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Pill className="w-5 h-5 text-blue-600" />
            {isEdit ? "Edit prescription" : "New prescription"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Doctor</Label>
              <Input value={doctorName} disabled className="mt-1.5 h-10 bg-slate-50" data-testid="rx-doctor-input" />
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-10" data-testid="rx-date-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Diagnosis</Label>
            <Input
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="e.g. Viral fever, hypertension"
              className="mt-1.5 h-10"
              data-testid="rx-diagnosis-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Medicines</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addRow} className="h-7 text-xs" data-testid="rx-add-medicine-btn">
                <Plus className="w-3 h-3 mr-1" /> Add row
              </Button>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[minmax(120px,1.6fr)_minmax(80px,0.9fr)_minmax(90px,0.9fr)_minmax(90px,0.9fr)_minmax(110px,1.1fr)_36px] gap-2 px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <div>Medicine</div>
                <div>Dosage</div>
                <div>Frequency</div>
                <div>Duration</div>
                <div>Instructions</div>
                <div />
              </div>
              {medicines.map((m, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[minmax(120px,1.6fr)_minmax(80px,0.9fr)_minmax(90px,0.9fr)_minmax(90px,0.9fr)_minmax(110px,1.1fr)_36px] gap-2 px-3 py-2 border-t border-slate-100 items-center"
                  data-testid={`rx-row-${i}`}
                >
                  <Input
                    value={m.name}
                    onChange={(e) => updateRow(i, "name", e.target.value)}
                    placeholder="e.g. Paracetamol"
                    className="h-9"
                    data-testid={`rx-name-${i}`}
                  />
                  <Input
                    value={m.dosage}
                    onChange={(e) => updateRow(i, "dosage", e.target.value)}
                    placeholder="500mg"
                    className="h-9"
                    data-testid={`rx-dosage-${i}`}
                  />
                  <Select value={m.frequency} onValueChange={(v) => updateRow(i, "frequency", v)}>
                    <SelectTrigger className="h-9" data-testid={`rx-frequency-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={m.duration}
                    onChange={(e) => updateRow(i, "duration", e.target.value)}
                    placeholder="5 days"
                    className="h-9"
                    data-testid={`rx-duration-${i}`}
                  />
                  <Input
                    value={m.instructions}
                    onChange={(e) => updateRow(i, "instructions", e.target.value)}
                    list={`rx-instr-${i}`}
                    placeholder="After food"
                    className="h-9"
                    data-testid={`rx-instructions-${i}`}
                  />
                  <datalist id={`rx-instr-${i}`}>
                    {INSTRUCTION_OPTIONS.map((opt) => <option key={opt} value={opt} />)}
                  </datalist>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(i)}
                    disabled={medicines.length === 1}
                    className="h-9 w-9"
                    data-testid={`rx-remove-${i}`}
                  >
                    <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Additional notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Drink plenty of fluids, rest, follow up in a week..."
              className="mt-1.5"
              data-testid="rx-notes-input"
            />
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="rx-error">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="rx-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save changes" : "Save prescription")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
